import { z } from "zod";
import pc from "picocolors";
import { match } from "ts-pattern";
import defaultFigures from "figures";
//#region src/lib/format/constants.ts
/**
* Unicode glyphs used in formatted output, frozen for immutability.
*/
const GLYPHS = Object.freeze({
	arrow: defaultFigures.pointerSmall,
	check: defaultFigures.tick,
	corner: defaultFigures.lineUpRight,
	cross: defaultFigures.cross,
	dash: defaultFigures.line,
	dot: defaultFigures.dot,
	fix: defaultFigures.lozenge,
	pipe: defaultFigures.lineVertical,
	skip: defaultFigures.circle,
	warning: defaultFigures.warning
});
//#endregion
//#region src/lib/format/duration.ts
/**
* Format a duration in milliseconds to a human-readable string.
*
* @param ms - Duration in milliseconds.
* @returns A formatted duration string.
*/
function formatDuration(ms) {
	return match(ms).when((v) => v < 1, () => "< 1ms").when((v) => v < 1e3, (v) => `${Math.round(v)}ms`).when((v) => v < 6e4, (v) => `${(v / 1e3).toFixed(2)}s`).otherwise((v) => {
		const minutes = Math.floor(v / 6e4);
		const seconds = Math.round(v % 6e4 / 1e3);
		return `${String(minutes)}m ${String(seconds)}s`;
	});
}
//#endregion
//#region src/lib/format/check.ts
/**
* Zod schema for validating a check status at the public API boundary.
*
* @private
*/
const CheckStatusSchema = z.enum([
	"pass",
	"fail",
	"warn",
	"skip",
	"fix"
]);
/**
* Format a single pass/fail/warn check row.
*
* @param input - The check data to format.
* @returns A formatted check string.
*/
function formatCheck(input) {
	const parsedStatus = CheckStatusSchema.safeParse(input.status);
	if (!parsedStatus.success) return `[Invalid check status: ${parsedStatus.error.issues.map((issue) => issue.message).join(", ")}]`;
	return ` ${match(input.status).with("pass", () => pc.green(GLYPHS.check)).with("fail", () => pc.red(GLYPHS.cross)).with("warn", () => pc.yellow(GLYPHS.warning)).with("skip", () => pc.gray(GLYPHS.skip)).with("fix", () => pc.blue(GLYPHS.fix)).exhaustive()} ${match(input.status).with("fail", () => pc.red(input.name)).with("warn", () => pc.yellow(input.name)).with("skip", () => pc.gray(input.name)).otherwise(() => input.name)}${formatOptionalDetail(input.detail)}${formatOptionalDuration(input.duration)}${formatOptionalHint(input.hint)}`;
}
/**
* Format optional detail text.
*
* @private
* @param detail - Optional detail string.
* @returns Formatted detail or empty string.
*/
function formatOptionalDetail(detail) {
	return match(detail).with(void 0, () => "").otherwise((d) => ` ${pc.gray(d)}`);
}
/**
* Format optional duration for a check row.
*
* @private
* @param duration - Optional duration in milliseconds.
* @returns Formatted duration or empty string.
*/
function formatOptionalDuration(duration) {
	return match(duration).with(void 0, () => "").otherwise((d) => ` ${pc.gray(`(${formatDuration(d)})`)}`);
}
/**
* Format optional hint text.
*
* @private
* @param hint - Optional hint string.
* @returns Formatted hint or empty string.
*/
function formatOptionalHint(hint) {
	return match(hint).with(void 0, () => "").otherwise((h) => ` ${pc.dim(`[${h}]`)}`);
}
//#endregion
//#region src/lib/format/code-frame.ts
/**
* Zod schema for validating a code frame annotation at the public API boundary.
*
* @private
*/
const CodeFrameAnnotationSchema = z.object({
	column: z.number().int().positive(),
	length: z.number().int().positive(),
	line: z.number().int().positive(),
	message: z.string()
});
/**
* Format an annotated code frame (oxlint style).
*
* @param input - The code frame data to format.
* @returns A formatted code frame string.
*/
function formatCodeFrame(input) {
	const parsed = CodeFrameAnnotationSchema.safeParse(input.annotation);
	if (!parsed.success) return `  ${pc.red("✗")} Invalid code frame annotation: ${parsed.error.issues.map((issue) => issue.message).join(", ")}`;
	const { annotation, filePath, lines, startLine } = input;
	const lastLine = startLine + lines.length - 1;
	if (annotation.line < startLine || annotation.line > lastLine) return `[Invalid annotation: line ${String(annotation.line)} is out of range ${String(startLine)}–${String(lastLine)}]`;
	const gutterWidth = String(lastLine).length;
	const header = `  ${pc.cyan(GLYPHS.arrow)} ${pc.cyan(`${filePath}:${String(annotation.line)}:${String(annotation.column)}`)}`;
	const separator = `  ${" ".repeat(gutterWidth)} ${pc.cyan(GLYPHS.pipe)}`;
	const codeLines = lines.map((line, idx) => {
		const lineNum = startLine + idx;
		return `  ${pc.cyan(String(lineNum).padStart(gutterWidth))} ${pc.cyan(GLYPHS.pipe)} ${line}`;
	});
	const annotationLineIdx = annotation.line - startLine;
	const pointer = " ".repeat(annotation.column - 1) + pc.red("^".repeat(annotation.length));
	const annotationRow = `  ${" ".repeat(gutterWidth)} ${pc.cyan(GLYPHS.pipe)} ${pointer} ${pc.red(annotation.message)}`;
	return [
		header,
		separator,
		...codeLines.reduce((acc, line, idx) => {
			if (idx === annotationLineIdx) return [
				...acc,
				line,
				annotationRow
			];
			return [...acc, line];
		}, []),
		separator
	].join("\n");
}
//#endregion
//#region src/lib/format/finding.ts
/**
* Format a full finding (oxlint style).
*
* @param input - The finding data to format.
* @returns A formatted finding string.
*/
function formatFinding(input) {
	const severityLabel = match(input.severity).with("error", () => pc.red("error")).with("warning", () => pc.yellow("warning")).with("hint", () => pc.blue("hint")).exhaustive();
	const categoryText = formatCategory(input.category);
	const ruleText = pc.dim(`[${input.rule}]`);
	const header = `  ${severityLabel}${match(categoryText).with("", () => "").otherwise((text) => ` ${text}`)}: ${input.message} ${ruleText}`;
	const framePart = formatFramePart(input.frame);
	const helpPart = formatHelpPart(input.help);
	return [
		header,
		...framePart,
		...helpPart
	].join("");
}
/**
* Format the optional category text.
*
* @private
* @param category - Optional category string.
* @returns Formatted category or empty string.
*/
function formatCategory(category) {
	return match(category).with(void 0, () => "").otherwise((c) => pc.dim(`(${c})`));
}
/**
* Format the optional code frame part.
*
* @private
* @param frame - Optional code frame input.
* @returns Array with formatted frame line, or empty array.
*/
function formatFramePart(frame) {
	return match(frame).with(void 0, () => []).otherwise((f) => [`\n${formatCodeFrame(f)}`]);
}
/**
* Format the optional help text part.
*
* @private
* @param help - Optional help string.
* @returns Array with formatted help line, or empty array.
*/
function formatHelpPart(help) {
	return match(help).with(void 0, () => []).otherwise((h) => [`\n  ${pc.cyan(`${GLYPHS.corner}${GLYPHS.dash}`)} ${pc.cyan("help")}: ${h}`]);
}
//#endregion
//#region src/lib/format/tally.ts
/**
* Format a summary block.
*
* - `style: 'tally'` renders labeled stat rows aligned in a block.
* - `style: 'inline'` renders a pipe-separated one-liner.
*
* @param input - The summary data to format.
* @returns A formatted summary string.
*/
function formatSummary(input) {
	return match(input).with({ style: "tally" }, formatSummaryBlock).with({ style: "inline" }, formatSummaryInline).exhaustive();
}
/**
* Format a summary block with labeled stat rows.
*
* @private
* @param input - The summary block data.
* @returns A formatted summary block string.
*/
function formatSummaryBlock(input) {
	const maxWidth = input.stats.reduce((max, stat) => Math.max(max, stat.label.length), 0);
	return input.stats.map((stat) => `  ${stat.label.padEnd(maxWidth)}  ${stat.value}`).join("\n");
}
/**
* Format a summary inline as a pipe-separated one-liner.
*
* @private
* @param input - The summary inline data.
* @returns A formatted summary line string.
*/
function formatSummaryInline(input) {
	return `  ${input.stats.join(pc.gray(" | "))}`;
}
//#endregion
export { formatDuration as a, formatCheck as i, formatFinding as n, GLYPHS as o, formatCodeFrame as r, formatSummary as t };

//# sourceMappingURL=tally-CQivzeuB.js.map